import React, { useState, useEffect } from 'react';
import './Dashboard.css'; 
import Sidebar from './Sidebar';
import reviewService from '../../api/reviewService';

const AdminReview = () => {
  const [isToggled, setIsToggled] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  // Review Data State
  const [reviewData, setReviewData] = useState([]);

  // --- API LOGIC ---
  const fetchReviews = async () => {
    try {
      setLoading(true);
      const data = await reviewService.getAllReview();
      setReviewData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  // Updated function to toggle visibility
  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = !currentStatus;
    const actionText = newStatus ? "show" : "hide";

    try {
      // Call service to update isActive status
      await reviewService.updateReview(id, { isActive: newStatus });
      
      // Update local state so the UI reflects the change immediately
      setReviewData(prev => 
        prev.map(item => item._id === id ? { ...item, isActive: newStatus } : item)
      );
    } catch (error) {
      console.error("Update failed:", error);
      alert(`Failed to ${actionText} review.`);
    }
  };

  // --- UI LOGIC ---
  const filteredReviews = reviewData.filter(review => {
    const name = review.name?.toLowerCase() || "";
    const email = review.email?.toLowerCase() || "";
    const feedback = review.feedback?.toLowerCase() || "";
    const term = searchTerm.toLowerCase();

    return name.includes(term) || email.includes(term) || feedback.includes(term);
  });

  const lastIndex = currentPage * recordsPerPage;
  const firstIndex = lastIndex - recordsPerPage;
  const currentRecords = filteredReviews.slice(firstIndex, lastIndex);
  const nPages = Math.ceil(filteredReviews.length / recordsPerPage);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <i key={i} className={`bi ${i < rating ? 'bi-star-fill text-warning' : 'bi-star text-muted'} me-1`} style={{ fontSize: '12px' }}></i>
    ));
  };

  return (
    <div className={`d-flex ${isToggled ? 'toggled' : ''}`} id="wrapper">
      <Sidebar />

      <div id="page-content-wrapper" className="bg-light flex-grow-1">
        <nav className="navbar navbar-expand-lg navbar-light bg-white border-bottom px-4 shadow-sm sticky-top">
          <div className="d-flex align-items-center w-100">
            <i className="bi bi-list fs-4 me-3 cursor-pointer" onClick={() => setIsToggled(!isToggled)}></i>
            <h5 className="mb-0 fw-bold">Management Panel</h5>
            <div className="ms-auto">
                <span className="text-muted small fw-medium">Admin / Review Visibility</span>
            </div>
          </div>
        </nav>

        <div className="container-fluid p-4">
          <div className="card border-0 shadow-sm" style={{ borderRadius: '15px', overflow: 'hidden' }}>
            
            <div className="card-header bg-white border-0 pt-4 px-4 pb-2">
              <div className="mb-3">
                <h6 className="fw-bold mb-0" style={{ color: '#1e293b', fontSize: '18px' }}>User Reviews</h6>
                <p className="text-muted mb-0" style={{ fontSize: '12.5px' }}>
                  Manage which reviews are visible on your public website.
                </p>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="input-group shadow-sm" style={{ width: '400px', borderRadius: '10px', overflow: 'hidden' }}>
                  <span className="input-group-text bg-white border-end-0 px-3">
                    <i className="bi bi-search text-muted"></i>
                  </span>
                  <input 
                    type="text" 
                    className="form-control border-start-0 ps-1" 
                    placeholder="Search reviews..." 
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  />
                </div>
              </div>
            </div>
            
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="bg-light">
                    <tr className="text-muted text-uppercase" style={{ fontSize: '11px', fontWeight: '700' }}>
                      <th className="ps-4 py-3">S.No</th>
                      <th>Reviewer</th>
                      <th>Rating</th>
                      <th>Feedback</th>
                      <th>Date</th>
                      <th className="text-center">Visibility</th>
                      <th className="text-end pe-4">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                       <tr><td colSpan="7" className="text-center py-5">Loading reviews...</td></tr>
                    ) : currentRecords.length > 0 ? (
                      currentRecords.map((item, index) => (
                        <tr key={item._id}>
                          <td className="ps-4 text-muted">{firstIndex + index + 1}</td>
                          <td className="py-3">
                            <div className="fw-bold" style={{ fontSize: '14px', color: '#1e293b' }}>{item.name}</div>
                            <div className="text-primary small">{item.email}</div>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              {renderStars(item.rating)}
                            </div>
                          </td>
                          <td style={{ maxWidth: '300px' }}>
                            <div className="text-truncate small" title={item.feedback}>{item.feedback}</div>
                          </td>
                          <td className="small text-muted">
                            {new Date(item.createdAt).toLocaleDateString()}
                          </td>
                          <td className="text-center">
                            <span className={`badge rounded-pill ${item.isActive !== false ? 'bg-success-subtle text-success border-success' : 'bg-secondary-subtle text-secondary border-secondary'} border px-3`}>
                              {item.isActive !== false ? 'Public' : 'Hidden'}
                            </span>
                          </td>
                          <td className="text-end pe-4">
                            {/* TOGGLE BUTTON */}
                            <button 
                              className={`btn btn-sm ${item.isActive !== false ? 'btn-outline-secondary' : 'btn-primary'} shadow-sm fw-bold`}
                              onClick={() => handleToggleStatus(item._id, item.isActive)}
                              style={{ minWidth: '80px', fontSize: '12px' }}
                            >
                              {item.isActive !== false ? (
                                <><i className="bi bi-eye-slash me-1"></i> Hide</>
                              ) : (
                                <><i className="bi bi-eye me-1"></i> Show</>
                              )}
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="7" className="text-center py-5 text-muted">No reviews found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card-footer bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center">
              <small className="text-muted">Showing {currentRecords.length} entries</small>
              <nav>
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setCurrentPage(prev => prev - 1)}>Previous</button>
                  </li>
                  {[...Array(nPages)].map((_, i) => (
                    <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                    </li>
                  ))}
                  <li className={`page-item ${currentPage === nPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setCurrentPage(prev => prev + 1)}>Next</button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminReview;